import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from "firebase/functions";
import tpcLogo from "../../../images/TPC-logo-light.png";
import './EmailValidationModal.css';

const EmailValidationModal = ({ 
  isOpen, 
  onClose, 
  email, 
  name, 
  surname, 
  userRole,
  onValidationSuccess 
}) => {
  const [validationCode, setValidationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // 'exists', 'waiting', 'new'
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const fn = getFunctions();

  useEffect(() => {
    if (isOpen) {
      checkEmailStatus();
      startResendCountdown();
    }
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const checkEmailStatus = async () => {
    setIsLoading(true);
    setError('');
    try {
      const checkEmail = httpsCallable(fn, "checkEmailStatus");
      const { data } = await checkEmail({ email });
      
      if (data.exists) {
        if (data.isValidated) {
          setEmailStatus('exists');
          setError('This email is already registered and validated. Please login instead.');
        } else if (data.hasValidationCode) {
          setEmailStatus('waiting');
          setSuccess('Your email exists and has a pending validation code. Please enter the 6-digit code sent to your email.');
        } else {
          setEmailStatus('new');
          setSuccess('Email not found. You can proceed with registration.');
        }
      } else {
        setEmailStatus('new');
        setSuccess('Email not found. You can proceed with registration.');
      }
    } catch (err) {
      console.error('Email status check error:', err);
      setError('Unable to check email status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateNewCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const createVal = httpsCallable(fn, "createHeroValidation");
      const { data: valData } = await createVal({
        email,
        name,
        surname,
        userRole,
      });

      const html = valData?.html ?? valData?.data?.html;
      if (!html) throw new Error("Validation HTML not returned from CF");

      const sendMail = httpsCallable(fn, "sendEmail");
      await sendMail({
        to: "marnus@tpc.training,conrad@tpc.training,clintonbonganikhoza@gmail.com",
        from: "Hero Validation <noreply@mail.andus.co.za>",
        subject: "NEW Hero-Dashboard applicant – action required",
        isHTML: true,
        html,
      });

      setSuccess('New validation code has been sent to the managers. Please contact them for the code.');
      setEmailStatus('waiting');
      startResendCountdown();
    } catch (err) {
      console.error('Generate code error:', err);
      setError(err.message ?? 'Could not generate new validation code.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    if (validationCode.length !== 6) {
      setError('Please enter a 6-digit validation code.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const verify = httpsCallable(fn, "verifyHeroValidation");
      const { data: res } = await verify({ email, validationCode: validationCode.trim() });

      if (!res.success) {
        setError('Incorrect validation code. Please try again.');
        return;
      }

      setSuccess('Validation successful! You can now complete your registration.');
      setTimeout(() => {
        onValidationSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Code verification error:', err);
      setError(err.message ?? 'Could not verify validation code.');
    } finally {
      setIsLoading(false);
    }
  };

  const startResendCountdown = () => {
    setCanResend(false);
    setResendCountdown(60); // 60 seconds countdown
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    await generateNewCode();
  };

  if (!isOpen) return null;

  return (
    <div className="email-validation-overlay" onClick={onClose}>
      <div className="email-validation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-background">
          <img src={tpcLogo} alt="TPC Logo" className="modal-logo" />
        </div>
        
        <div className="modal-header">
          <h2 className="modal-title">Email Validation</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          <div className="email-info">
            <p className="email-text">Email: <strong>{email}</strong></p>
            <p className="name-text">Name: <strong>{name} {surname}</strong></p>
            <p className="role-text">Role: <strong>{userRole}</strong></p>
          </div>

          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Processing...</p>
            </div>
          )}

          {error && <div className="modal-error">{error}</div>}
          {success && <div className="modal-success">{success}</div>}

          {emailStatus === 'exists' && (
            <div className="status-message">
              <p>This email is already registered and validated.</p>
              <p>Please use the login page to access your account.</p>
            </div>
          )}

          {emailStatus === 'waiting' && (
            <div className="validation-section">
              <h3>Enter Validation Code</h3>
              <p>Please enter the 6-digit validation code provided by the managers.</p>
              
              <div className="code-input-container">
                <input
                  type="text"
                  pattern="\d{6}"
                  maxLength="6"
                  placeholder="Enter 6-digit code"
                  className="code-input"
                  value={validationCode}
                  onChange={(e) => setValidationCode(e.target.value.replace(/\D/g, ''))}
                />
                <button 
                  className="verify-button" 
                  onClick={verifyCode}
                  disabled={validationCode.length !== 6 || isLoading}
                >
                  Verify Code
                </button>
              </div>

              <div className="resend-section">
                <p>Didn't receive the code?</p>
                <button 
                  className="resend-button" 
                  onClick={handleResendCode}
                  disabled={!canResend || isLoading}
                >
                  {canResend ? 'Resend Code' : `Resend in ${resendCountdown}s`}
                </button>
              </div>
            </div>
          )}

          {emailStatus === 'new' && (
            <div className="new-user-section">
              <h3>New User Registration</h3>
              <p>This email is not registered. You can proceed with registration.</p>
              <button 
                className="generate-code-button" 
                onClick={generateNewCode}
                disabled={isLoading}
              >
                Generate Validation Code
              </button>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="modal-button secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailValidationModal; 